import {useCallback, useEffect, useMemo, useRef} from 'react';

import {getLocationHash} from 'BenchlingUtil';
import * as logging from 'util/Logging';

export const DEFAULT_NAVIGATION_PARAM = 'anchor';

interface AnchorConfig {
  /** Hash parameter name (e.g., 'anchor', 'puuid', 'section') */
  hashParam: string;
  /** Element attribute name (e.g., 'data-scroll-id') */
  elementAttr: string;
  /** Function to transform the anchor value before searching for the element */
  valueFn?: (anchorValue: string) => string | undefined;
}

export interface UseHashNavigationOptions {
  /**
   * The container element to search for anchor elements within.
   */
  container?: HTMLElement | null;

  /**
   * Anchor configuration for hash navigation.
   */
  anchorConfig: AnchorConfig;
}

interface HashNavigationState {
  currentHash: string;
  currentAnchorValue: string | null;
  hasNavigationHash: () => boolean; // Instance-specific checker
}

/**
 * Utility function to check if the current URL hash contains any navigation parameters.
 * This is useful for components that need to know if hash-based navigation is active
 * without instantiating the full useHashNavigation hook.
 *
 * @param additionalParams Optional array of additional parameter names to check for
 * @returns true if any navigation hash parameters are present
 */
export function hasAnyNavigationHash(additionalParams: Array<string> = []): boolean {
  const hash = window.location.hash;
  if (!hash || hash === '#') {
    return false;
  }

  // Check for the default navigation parameter + any additional parameters
  const allParams = [
    DEFAULT_NAVIGATION_PARAM, // 'anchor'
    ...additionalParams,
  ];

  // Parse the hash as URLSearchParams for exact parameter matching
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
  return allParams.some((param) => hashParams.has(param));
}

/**
 * Hook for handling URL hash navigation to anchor sections.
 *
 * Uses a consistent hash format: #anchor=some-value-here
 *
 * @options
 * - container (HTMLElement | null): The container element within which to search for anchor elements.
 * - anchorConfig (AnchorConfig): Configuration object for anchor navigation, including:
 *     - hashParam (string): The name of the hash parameter to look for in the URL (e.g., 'anchor' which is the default).
 *     - elementAttr (string): The attribute on elements to match against the anchor value (e.g., 'data-anchor-value' which is the default).
 *     - valueFn? (function): Optional function to transform the anchor value before searching for the element.
 *
 * @param options Configuration for hash navigation behavior
 * @returns Current hash state and navigation utilities
 */
export default function useHashNavigation(options: UseHashNavigationOptions): HashNavigationState {
  const {container, anchorConfig: config} = options;

  // Track the last processed hash to prevent unnecessary re-scrolling
  const lastProcessedRef = useRef<string | null>(null);

  // Generic implementation for finding elements by anchor value
  const findElementByAnchorValue = useCallback(
    (anchorValue?: string): HTMLElement | null => {
      if (!container) {
        return null;
      }
      if (config.valueFn && anchorValue) {
        anchorValue = config.valueFn(anchorValue);
      }

      const contentSelector = `[${config.elementAttr}="${anchorValue}"]`;

      const contentElements: Array<HTMLElement> = Array.from(container.querySelectorAll(contentSelector));

      if (contentElements.length === 0) {
        logging.info(
          `useHashNavigation: Could not find the section with ${config.elementAttr}: ${anchorValue}`
        );
      }
      return contentElements[0] || null;
    },
    [container, config]
  );

  // Extract hash parsing logic
  const parseHashForTargetId = useCallback((): string | null => {
    const hashParams = new URLSearchParams(getLocationHash());
    return hashParams.get(config.hashParam);
  }, [config.hashParam]);

  // Instance-specific hasNavigationHash function
  const hasNavigationHashForInstance = useCallback(
    (): boolean => new URLSearchParams(getLocationHash()).has(config.hashParam),
    [config.hashParam]
  );

  // Process the hash and navigate to the target section
  const jumpToAnchor = useCallback(
    (isUserTriggered = false): void => {
      const currentHash = getLocationHash();
      const targetId = parseHashForTargetId();

      if (!targetId) {
        // If the hash is empty, and this is a hash change (isUserTriggered == true),
        // scroll to the top of the document.
        if (isUserTriggered) {
          if (container) {
            container.scrollTo({top: 0, behavior: 'auto'});
          }
        }
        // Update tracking ref for empty hash
        lastProcessedRef.current = currentHash || null;
        return;
      }

      const targetElement = findElementByAnchorValue(targetId);
      if (targetElement) {
        // Check if we've already processed this hash
        if (!isUserTriggered && lastProcessedRef.current === currentHash) {
          // We've already processed this hash, skip navigation
          return;
        }

        requestAnimationFrame(() => {
          targetElement.scrollIntoView({block: 'start', behavior: 'auto'});
          // Avoid odd browser bug where the entire page is scrolled if height is small.
          // Taken from https://github.com/benchling/aurelia/blob/dev/client/ui-library/useDocumentScrollState.ts#L101
          window.scrollTo(0, 0);
        });

        // Update tracking ref after successful navigation
        lastProcessedRef.current = currentHash || null;
      }
    },
    [findElementByAnchorValue, parseHashForTargetId, container]
  );

  useEffect(() => {
    if (!container) {
      return;
    }

    // Handle initial hash on mount
    if (hasNavigationHashForInstance()) {
      jumpToAnchor(false);
    }

    // Set up event listeners for subsequent changes
    const handleHashChange = (): void => {
      // Clear tracking ref on user-triggered hash changes to ensure navigation always works
      lastProcessedRef.current = null;
      jumpToAnchor(true);
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [jumpToAnchor, container, hasNavigationHashForInstance]);

  return {
    currentHash: window.location.hash,
    currentAnchorValue: useMemo(() => parseHashForTargetId(), [parseHashForTargetId]),
    hasNavigationHash: hasNavigationHashForInstance,
  };
}
